import { Component, computed, inject, OnInit, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address, AddressFields, PLACE_TYPES } from '../../core/models/address.model';
import { Interaction } from '../../core/models/interaction.model';
import { AddressService } from '../../core/services/address.service';
import { AuthService } from '../../core/services/auth.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { InteractionService } from '../../core/services/interaction.service';
import { LoggerService } from '../../core/services/logger.service';
import { PermissionService } from '../../core/services/permission.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Create / edit an address. The structured fields are the primary entry —
 * "Full address" at the bottom is auto-composed from them live, and only
 * overridden if the user types into it directly (see {@link syncFormattedAddress}).
 * This is one-way (structured -> formatted): typing a full address does not
 * populate the structured fields, since parsing free text back into
 * components reliably is a much bigger problem than this phase needs.
 */
@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    ButtonComponent,
    ModalDialogComponent,
    SpinnerComponent,
    TippyDirective,
    TranslatePipe,
  ],
  templateUrl: './address-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './address-form.component.css',
})
export class AddressFormComponent implements OnInit {
  private addressService = inject(AddressService);
  private interactionService = inject(InteractionService);
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private translate = inject(TranslationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private userStorage = inject(UserStorageService);
  private log = inject(LoggerService).getLogger('address-form');

  /**
   * City and region rarely change between consecutive addresses for one
   * canvasser working an area, so a new address starts pre-filled with
   * whichever was last saved -- see {@link rememberLocation}. Per-device via
   * UserStorageService, not synced: this is a data-entry convenience, not
   * data about the address itself.
   */
  private static readonly LAST_CITY_KEY = 'address_last_city';
  private static readonly LAST_REGION_KEY = 'address_last_region';

  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  formValid = signal(false); // avoids ExpressionChangedAfterItHasBeenCheckedError

  saveDisabled = computed(() => !this.formValid() || this.saving());

  private loadedAddress = signal<Address | null>(null);
  interactions: Signal<Interaction[]> = signal([]);
  hasInteractions = computed(() => this.interactions().length > 0);
  /**
   * Whether the delete button should appear at all: creator of the address,
   * or anyone with the blanket `address.delete` permission (admin/org-owner)
   * -- see CLAUDE.md's canvassing-crm design doc §5 on per-org address
   * ownership; there's no cross-org concern since addresses are entirely
   * org-local. Separate from {@link hasInteractions}, which governs whether
   * that button is enabled -- it must stay visible-but-disabled so an owner
   * can see *why* they can't delete a pin, rather than the option silently
   * disappearing (users were reporting this as "the button is missing").
   */
  canManageAddressDelete = computed(() => {
    if (!this.isEditMode()) return false;
    const address = this.loadedAddress();
    if (!address) return false;
    const isOwner = address.created_by === this.authService.getCurrentUserId();
    return isOwner || this.permissionService.has('address.delete');
  });

  showDeleteDialog = signal(false);
  deleting = signal(false);
  deleteError = signal<string | null>(null);

  form = new FormGroup({});
  model: AddressFields = {
    formatted_address: '',
    unit: '',
    house_number: '',
    street: '',
    city: '',
    region: '',
    postal_code: '',
    country: '',
    place_type: 'unknown',
    business_name: '',
    notes: '',
  };
  fields: FormlyFieldConfig[] = [];

  /** The last value {@link syncFormattedAddress} wrote — see its doc comment. */
  private lastComposed = '';
  private addressId: string | null = null;

  async ngOnInit(): Promise<void> {
    // Defer initialization a tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    await Promise.resolve();

    this.fields = this.buildFields();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.addressId = id;
      this.isEditMode.set(true);
      await this.loadAddress(id);
    } else {
      this.model.city = this.userStorage.getItem(AddressFormComponent.LAST_CITY_KEY) ?? '';
      this.model.region = this.userStorage.getItem(AddressFormComponent.LAST_REGION_KEY) ?? '';
      this.breadcrumbService.setDynamicLabel(this.translate.translate('addresses.form.newTitle'));
      this.loading.set(false);
    }

    // By this point formly has built the controls and (in edit mode) synced
    // `this.model` into `this.form`, so `this.form.value` already reflects
    // the loaded record.
    this.lastComposed = this.composeFormattedAddress(this.form.value);
    this.form.valueChanges.subscribe((value) => this.syncFormattedAddress(value));

    this.form.statusChanges.subscribe(() => this.formValid.set(this.form.valid));
    this.formValid.set(this.form.valid);
  }

  private buildFields(): FormlyFieldConfig[] {
    const t = (key: string) => this.translate.translate(key);
    const text = (key: keyof AddressFields, labelKey: string, extraProps: Record<string, unknown> = {}): FormlyFieldConfig => ({
      key,
      type: 'text',
      props: { label: t(labelKey), attributes: { 'data-testid': `address-${key.replace(/_/g, '-')}-input` }, ...extraProps },
    });

    return [
      {
        key: 'place_type',
        type: 'native-select',
        props: {
          label: t('addresses.form.placeTypeLabel'),
          showLabel: true,
          options: PLACE_TYPES.map((value) => ({ value, label: t(`addresses.placeType.${value}`) })),
        },
      },
      {
        ...text('business_name', 'addresses.form.businessNameLabel'),
        // Business identity is meaningless for a residence — keep the field
        // out of the way until it's relevant. Its value is left in place if
        // the place type changes back later rather than cleared.
        expressions: { hide: (field) => field.model?.place_type !== 'business' },
      },
      text('unit', 'addresses.form.unitLabel'),
      text('house_number', 'addresses.form.houseNumberLabel'),
      text('street', 'addresses.form.streetLabel'),
      text('city', 'addresses.form.cityLabel'),
      text('region', 'addresses.form.regionLabel'),
      text('postal_code', 'addresses.form.postalCodeLabel'),
      text('country', 'addresses.form.countryLabel'),
      {
        key: 'notes',
        type: 'textarea',
        props: { label: t('addresses.form.notesLabel'), attributes: { 'data-testid': 'address-notes-input' } },
      },
      text('formatted_address', 'addresses.form.formattedAddressLabel', {
        required: true,
        description: t('addresses.form.formattedAddressHint'),
      }),
    ];
  }

  /**
   * Compose a display address from the structured fields, Canadian-style
   * (unit as a hyphenated prefix: "204-123 Main St").
   */
  private composeFormattedAddress(v: Partial<AddressFields>): string {
    const streetLine = [v.house_number, v.street].filter(Boolean).join(' ');
    const line1 = v.unit && streetLine ? `${v.unit}-${streetLine}` : v.unit || streetLine;
    const cityLine = [v.city, v.region].filter(Boolean).join(', ');
    return [line1, cityLine, v.postal_code, v.country]
      .map((s) => (s ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Keeps "Full address" composed from the structured fields, but only while
   * it's still "in sync" with what we last composed — the moment the user
   * types into it directly, it diverges from `lastComposed` and this stops
   * touching it. `emitEvent: false` on our own write keeps that write from
   * looking like a user edit on the next call — but it ALSO suppresses
   * Formly's own control->model sync for this control, so `this.model` has
   * to be updated by hand here too, or the composed text shows in the input
   * while `save()` (which reads `this.model`) persists an empty string.
   */
  private syncFormattedAddress(value: Partial<AddressFields>): void {
    const composed = this.composeFormattedAddress(value);
    const control = this.form.get('formatted_address') as FormControl<string> | null;
    if (control && value.formatted_address === this.lastComposed && composed !== value.formatted_address) {
      control.setValue(composed, { emitEvent: false });
      this.model.formatted_address = composed;
    }
    this.lastComposed = composed;
  }

  /** Remember city/region as the default for this device's next new address. */
  private rememberLocation(): void {
    if (this.model.city) this.userStorage.setItem(AddressFormComponent.LAST_CITY_KEY, this.model.city);
    if (this.model.region) this.userStorage.setItem(AddressFormComponent.LAST_REGION_KEY, this.model.region);
  }

  private async loadAddress(id: string): Promise<void> {
    try {
      const address = await this.addressService.getById(id);
      if (!address) {
        this.error.set(this.translate.translate('addresses.form.notFoundError'));
        return;
      }
      this.loadedAddress.set(address);
      this.interactions = this.interactionService.createQueryForAddress(id).data;
      this.breadcrumbService.setDynamicLabel(address.formatted_address);
      this.model = {
        formatted_address: address.formatted_address,
        unit: address.unit,
        house_number: address.house_number,
        street: address.street,
        city: address.city,
        region: address.region,
        postal_code: address.postal_code,
        country: address.country,
        place_type: address.place_type,
        business_name: address.business_name,
        notes: address.notes,
      };
    } catch (err) {
      this.log.error('Failed to load address', err);
      this.error.set(this.translate.translate('addresses.form.loadError'));
    } finally {
      this.loading.set(false);
    }
  }

  /** Absolute, not relative -- see InteractionComponent's equivalent comment
   *  for why relative '..' navigation is unsafe here. */
  onCancel(): void {
    if (this.addressId) {
      this.router.navigate(['/map/addresses', this.addressId]);
    } else {
      this.router.navigate(['/map/addresses']);
    }
  }

  async save(): Promise<void> {
    if (this.saveDisabled()) return;

    this.saving.set(true);
    this.error.set(null);

    try {
      if (this.isEditMode() && this.addressId) {
        await this.addressService.update({ id: this.addressId, ...this.model });
        this.rememberLocation();
        this.router.navigate(['/map/addresses', this.addressId]);
      } else {
        const created = await this.addressService.create(this.model);
        this.rememberLocation();
        this.router.navigate(['/map/addresses', created.id]);
      }
    } catch (err: unknown) {
      this.log.error('Failed to save address', err);
      this.error.set(err instanceof Error ? err.message : this.translate.translate('addresses.form.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  openDelete(): void {
    this.deleteError.set(null);
    this.showDeleteDialog.set(true);
  }

  cancelDelete(): void {
    this.showDeleteDialog.set(false);
  }

  async confirmDelete(): Promise<void> {
    if (!this.addressId) return;

    this.deleting.set(true);
    this.deleteError.set(null);
    try {
      const result = await this.addressService.delete(this.addressId);
      if (result.success) {
        this.showDeleteDialog.set(false);
        this.router.navigate(['/map/addresses']);
        return;
      }
      this.deleteError.set(
        this.translate.translate(
          result.reason === 'has_interactions' ? 'addresses.form.deleteHasInteractionsError' : 'addresses.form.notFoundError',
        ),
      );
    } catch (err) {
      this.log.error('Failed to delete address', err);
      this.deleteError.set(this.translate.translate('addresses.form.deleteError'));
    } finally {
      this.deleting.set(false);
    }
  }
}
